const TYPE = {
  CIRCLE: 'circle',
  SEMICIRCLE: 'semicircle',
  LINE: 'line'
};

class ProgressBarWrapper  {
  constructor(progressBars) {
    this.progressBars = progressBars;

    if (this.progressBars.length <= 0) return;

    // IntersectionObserver to initialize when the progress bars are in the viewport.
    this.observerConfig = {
      root: null,
      rootMargin: '0px',
      threshold: 1.0
    }

    this.observerFn = (entries, observer) => {
      entries.forEach( entry => {
        const { target: progressBar } = entry;

        if (entry.isIntersecting) {
          this.initProgressBar(progressBar);
          observer.unobserve(progressBar);
        }

      });
    }

    this.observer = new IntersectionObserver(this.observerFn, this.observerConfig);

    this.init();
  }

  init() {
    this.progressBars.forEach( progressBar => {
      this.observer.observe(progressBar);
    });
  }

  initFallback() {
    this.progressBars.forEach( progressBar => {
      const fallbackSrc = progressBar.dataset.fallback ? progressBar.dataset.fallback : false;
      if (!fallbackSrc) return;

      let fallbackImg = document.createElement('img');
      fallbackImg.src = fallbackSrc;
      progressBar.appendChild(fallbackImg);
    });
  }

  /**
   * Initializes an individual progress bar.
   * @param {*} progressBar 
   */
  initProgressBar(progressBar) {

    /**
     * Options available:
     * type         - circle, semicircle, line - circle
     * value        - The final value to show - default value 50
     * valueFormat  - currency, percentage
     * showValue    - True/False Should the value be shown. - true
     * percentage   - The final percentage to show between 1 through 100 - 50
     */

    // 1. Get the options
    let options = this.getOptions(progressBar.dataset.options);
    let percentage = options.percentage / 100;
    let tempProgressBar;

    // 2. Initiate the progress bar.
    switch (options.type) {
      case TYPE.CIRCLE:
        tempProgressBar = new ProgressBar.Circle(progressBar, options);
        break;

      case TYPE.SEMICIRCLE:
        tempProgressBar = new ProgressBar.SemiCircle(progressBar, options);
        break;

      case TYPE.LINE:
        tempProgressBar = new ProgressBar.Line(progressBar, options);
        break;

      default:
        break;
    }

    tempProgressBar.animate(percentage)
  }

  /**
   * Combines the passed options with the default options.
   * @param {*} options 
   */
  getOptions(options) {
    let tempOptions;

    // 1. Get the options
    try {
      tempOptions = options ? JSON.parse(options) : false;
    } catch(error) {
      console.log('progressbarwrapper.class.js:', error);
      return false;
    }

    // 2. Combine the default options with the passed options
    switch (tempOptions.type) {
      case TYPE.CIRCLE:
        tempOptions = Object.assign(this.getCircleOptions(tempOptions), tempOptions);
        break;

      case TYPE.SEMICIRCLE:
        tempOptions = Object.assign(this.getSemiCircleOptions(tempOptions), tempOptions);
        break;

      case TYPE.LINE:
        tempOptions = Object.assign(this.getLineOptions(tempOptions), tempOptions);
        break;

      default:
        break;
    }

    return tempOptions;
  }

  /**
   * Gets the default value for circles
   */
  getCircleOptions(tempOptions) {
    const { value: finalValue, valueFormat = '', percentage, prefix = '', suffix = '' } = tempOptions;

    return {
      // Custom
      type: 'circle',
      color: '#aaa',
      // This has to be the same size as the maximum width to
      // prevent clipping
      strokeWidth: 4,
      trailWidth: 1,
      easing: 'easeInOut',
      duration: 1400,
      text: {
        autoStyleContainer: false,
      },
      from: { color: '#aaa', width: 1 },
      to: { color: '#333', width: 4 },

      // Set default step function for all animate calls
      step: (state, circle) => {
        circle.path.setAttribute('stroke', state.color);
        circle.path.setAttribute('stroke-width', state.width);

        var value = `${prefix}` + this.formatValue(Math.round(circle.value() * (parseInt(finalValue) * 100 / percentage)), valueFormat) + `${suffix}`;
        if (value === 0) {
          circle.setText('');
        } else {
          circle.setText(value);
        }
      }
    }
  }

    /**
   * Gets the default value for circles
   */
  getSemiCircleOptions(tempOptions) {
    const { value: finalValue, valueFormat = '', percentage, prefix = '', suffix = '' } = tempOptions;

    return {
      strokeWidth: 6,
      color: '#FFEA82',
      trailColor: '#eee',
      trailWidth: 1,
      easing: 'easeInOut',
      duration: 1400,
      svgStyle: null,
      text: {
        value: '',
        alignToBottom: false,
      },
      from: {color: '#FFEA82'},
      to: {color: '#ED6A5A'},
      // Set default step function for all animate calls
      step: (state, bar) => {
        bar.path.setAttribute('stroke', state.color);
        var value = `${prefix}` + this.formatValue(Math.round(bar.value() * (parseInt(finalValue) * 100 / percentage)), valueFormat) + `${suffix}`;
        if (value === 0) {
          bar.setText('');
        } else {
          bar.setText(value);
        }
      }
    }
  }

    /**
   * Gets the default value for circles
   */
  getLineOptions(tempOptions) {
    const { value: finalValue, valueFormat = '', percentage, prefix = '', suffix = '' } = tempOptions;

    return {
      // Custom
      type: 'line',

      // Existing
      strokeWidth: 4,
      easing: 'easeInOut',
      duration: 1400,
      color: '#FFEA82',
      trailColor: '#eee',
      trailWidth: 1,
      svgStyle: {width: '100%', height: '100%'},
      text: {
        style: {
          // Text color.
          // Default: same as stroke color (options.color)
          color: '#999',
          transform: null
        },
        autoStyleContainer: false
      },
      from: {color: '#FFEA82'},
      to: {color: '#ED6A5A'},
      step: (state, bar) => {
        bar.setText(`${prefix}` + this.formatValue(Math.round(bar.value() * (parseInt(finalValue) * 100 / percentage)), valueFormat) + `${suffix}`);
      }
    }
  }

  formatValue(value, format) {
    let tempValue = value;

    switch (format) {
      case 'currency':
        tempValue = `<span class="format-symbol">$</span>` + this.numberWithCommas(value);
        break;

      case 'percentage':
        tempValue = value + '<span class="format-symbol">%</span>';
        break;

      default:
        break;
    }

    return tempValue;
  }

  numberWithCommas(x) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

}